import { create } from 'zustand'
import type { Usuario, Parceiro, ParceiroVinculado } from '@/types'
import * as usuariosApi from '@/api/usuarios'
import * as parceirosApi from '@/api/parceiros'

interface AuthState {
  usuario: Usuario | null
  parceiro: Parceiro | null
  /** Todos os vínculos do usuário. Vazio para administrador da New Driver, que alcança qualquer um. */
  parceiros: ParceiroVinculado[]
  isAuthenticated: boolean
  isAdministrador: boolean
  /** Equipe New Driver que altera cartão e logo do certificado. Sem isto, a equipe só vê. */
  podeConfigurarNewDriver: boolean
  /** Entrou com a senha padrão — os 6 primeiros dígitos do CPF. Só o login sabe disso. */
  usandoSenhaPadrao: boolean
  loading: boolean
  login: (cpf: string, senha: string) => Promise<void>
  alterarSenha: (senhaAtual: string, novaSenha: string) => Promise<void>
  logout: () => Promise<void>
  checkSession: () => Promise<void>
  entrarComLoginToken: (loginToken: string) => Promise<void>
  selecionarParceiro: (parceiroId: number) => Promise<void>
}

const DESLOGADO = {
  usuario: null,
  parceiro: null,
  parceiros: [],
  isAuthenticated: false,
  isAdministrador: false,
  podeConfigurarNewDriver: false,
  usandoSenhaPadrao: false,
}

export const useAuthStore = create<AuthState>((set) => ({
  ...DESLOGADO,
  loading: true,

  login: async (cpf, senha) => {
    const data = await usuariosApi.login(cpf, senha)
    set({
      usuario: data.usuario,
      parceiro: data.parceiro,
      parceiros: data.parceiros ?? [],
      isAuthenticated: true,
      isAdministrador: data.isAdministrador ?? false,
      podeConfigurarNewDriver: data.podeConfigurarNewDriver ?? false,
      usandoSenhaPadrao: data.usandoSenhaPadrao ?? false,
    })
  },

  alterarSenha: async (senhaAtual, novaSenha) => {
    await usuariosApi.alterarSenha(senhaAtual, novaSenha)
    set({ usandoSenhaPadrao: false })
  },

  entrarComLoginToken: async (loginToken) => {
    const data = await usuariosApi.trocarLoginTokenPorSessao(loginToken)
    set({
      usuario: data.usuario,
      parceiro: data.parceiro,
      parceiros: data.parceiros ?? [],
      isAuthenticated: true,
      isAdministrador: data.isAdministrador ?? false,
      podeConfigurarNewDriver: data.podeConfigurarNewDriver ?? false,
      loading: false,
    })
  },

  logout: async () => {
    await usuariosApi.logout()
    set({ ...DESLOGADO })
  },

  checkSession: async () => {
    try {
      const data = await usuariosApi.getSession()
      if (data.usuario) {
        set({
          usuario: data.usuario,
          parceiro: data.parceiro,
          parceiros: data.parceiros ?? [],
          isAuthenticated: true,
          isAdministrador: data.isAdministrador ?? false,
          podeConfigurarNewDriver: data.podeConfigurarNewDriver ?? false,
          loading: false,
        })
      } else {
        set({ ...DESLOGADO, loading: false })
      }
    } catch {
      set({ ...DESLOGADO, loading: false })
    }
  },

  selecionarParceiro: async (parceiroId) => {
    const data = await parceirosApi.selecionarParceiro(parceiroId)
    set({ parceiro: data.parceiro })
  },
}))

/** Só quem tem mais de um vínculo pode trocar; com um só, não há para onde ir. */
export function usePodeTrocarParceiro(): boolean {
  const isAdministrador = useAuthStore((s) => s.isAdministrador)
  const quantidadeDeVinculos = useAuthStore((s) => s.parceiros.length)
  return isAdministrador || quantidadeDeVinculos > 1
}
